// ── 계약 저장 (ContractModal 에서 분리) ──────────────────────────────────────
// ContractModal.jsx 가 363줄이라 300줄 규칙에 걸려, 가장 큰 덩어리인 doSave 를
//   **글자 그대로** 옮겼다(2026-09-22). 동작 변화 0 — 모달은 인자만 넘겨 호출한다.
import { apiClient } from '../../../api/apiClient';
import { savePromoOrder } from './contractPromoSave';
import { HQ_CONTRACT_API } from './contractApi';

/** form → contract_items 배열 (주계약 + 서비스) */
export function buildContractItems(form, ticketTypes) {
  return [
    ...(form.contractProducts || []).flatMap((name) => {
      const tt = ticketTypes.find((t) => t.name === name);
      if (!tt?.id) return [];
      return [{
        ticket_type_id: tt.id,
        validity_months: form.contractValidities?.[name] ? Number(form.contractValidities[name]) : null,
        // 장수를 비우면 0장으로 보낸다(2026-09-22). 예전 `|| 1` 은 빈 칸을 1장으로 바꿔
        //   적지 않은 장수가 합계에 잡혔다.
        quantity: Number(form.contractCounts?.[name]) > 0 ? Number(form.contractCounts[name]) : 0,
        is_service: false,
      }];
    }),
    ...(form.serviceProducts || []).flatMap((name) => {
      const tt = ticketTypes.find((t) => t.name === name);
      if (!tt?.id) return [];
      return [{
        ticket_type_id: tt.id,
        validity_months: form.serviceValidities?.[name] ? Number(form.serviceValidities[name]) : null,
        quantity: Number(form.serviceCounts?.[name]) > 0 ? Number(form.serviceCounts[name]) : 0,
        is_service: true,
      }];
    }),
  ];
}

/**
 * 계약 저장 — 신규 등록 / 콜에서 계약전환 / 기존 계약 수정 세 경로.
 * 성공하면 홍보물 주문까지 붙이고 true, 실패하면 false 를 돌려준다(모달이 닫힘 여부를 정한다).
 */
export async function saveContract({
  form, row, isNew, isReorder, deliveryDirty,
  ticketTypes, allProductCategories, showToast,
  api = HQ_CONTRACT_API, // 주소 묶음(contractApi.js, 2026-10-15) — 기본은 본사 주소
}) {
  const contract_items = buildContractItems(form, ticketTypes);
  const payload = {
    company_name: form.company_name, phone: form.phone,
    mobile_phone: form.mobile_phone, address: form.address,
    sales_person_name:    form.sales_person_name,
    contract_date:        form.contract_date || null,
    contract_items:       contract_items.length ? contract_items : null,
    feedback:             '계약',
    // 다중 배송 정보 (delivery_items 배열, 하위호환용 단일 컬럼은 백엔드에서 처리)
    // is_return: true이면 정산서에서 택배비 ×2 적용 (발송+회수)
    ...(form.contract_status != null ? { contract_status: form.contract_status } : {}),
    memo: form.memo || null,
    // 수정 시 신규/재주문 변경 반영
    is_reorder: form.registrationType === 'reorder',
  };
  if (deliveryDirty) {
    payload.delivery_items = (form.deliveryItems || []).map((i) => ({
      courier:         (i.courier || '').trim() || null,
      tracking_number: (i.tracking_number || '').trim() || null,
      is_return:       Boolean(i.is_return),
      is_pickup:       Boolean(i.is_pickup), // 직접수령 줄 — 서버가 택배사·송장번호·회수를 비워 저장(배송비 0원)
    }));
  }
  let res;
  const isRegisteringCall = !isNew && !row?.is_contracted && !row?.is_direct_contract;
  if (isNew) {
    const postPayload = { ...payload, user_name: form.user_name };
    // 재주문 토글이 선택된 경우 is_reorder=true 전송
    // tm_call_id: 기존 계약 행(row)이 있을 때만 전달 — 없으면 신규직접등록 경로로 처리됨
    if (form.registrationType === 'reorder') {
      postPayload.is_reorder = true;
      // tm_call 연결 계약만 원본 콜에 재주문을 붙인다.
      // ⚠️ 직접등록 계약(tm_call_id 없음)은 절대 계약 id 를 콜 id 로 재사용하지 않는다 —
      //    contracts.id 와 tm_calls.id 가 같은 번호로 겹치면 엉뚱한 콜(다른 고객)에 붙는다(과거 버그).
      //    tm_call_id 미전송 → 백엔드 contract-create.js 경로 B: 신규직접등록 + is_reorder=true.
      const linkedCallId = row?.tm_call_id ?? null;
      if (linkedCallId) postPayload.tm_call_id = linkedCallId;
    }
    // 통합 계약 화면: 콜 없이 직접 등록할 때 고른 회사(4-5, 결정 1-a — 서버가 메뉴 회사인지 다시 본다).
    //   본사·회사별 묶음은 createCompanies 가 null 이라 보내는 값이 전과 같다.
    if (Array.isArray(api.createCompanies) && form.contract_company_code) {
      postPayload.contract_company_code = form.contract_company_code;
    }
    res = await apiClient.post(api.contracts, postPayload);
  } else if (isRegisteringCall) {
    const tmCallId = row?.tm_call_id ?? row?.id;
    res = await apiClient.put(`${api.contracts}/from-call/${tmCallId}`, payload);
  } else {
    res = await apiClient.put(`${api.contracts}/${row.id}`, payload);
  }
  if (!res.ok) { showToast(res.data?.message || '저장에 실패했습니다.', 'error'); return false; }
  const msg = isReorder ? '재주문이 등록되었습니다.' : isNew ? '계약이 등록되었습니다.' : '계약 정보가 수정되었습니다.';
  showToast(msg, 'success');
  // 홍보물 주문 등록 및 계약 연결 (savePromoOrder 분리). 회사별·통합 화면은 보기만이라 건너뛴다(4-5, 결정 2-a).
  if (api.promoWritable === false) return true;
  const contractId = res.data?.contract_id
    ?? (isNew ? res.data?.call?.id : (row?.direct_contract_id ?? row?.id));
  const promoSaveOk = await savePromoOrder({ form, ticketTypes, allProductCategories, contractId, showToast, api });
  if (promoSaveOk === false) return false;
  return true;
}
